// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PaginationRight from "@flip/XDL/Icons/PaginationRight";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, getSystemColor, iconLinkCommonStyles, mergeWithCss, xScreen } from "@flip/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import { LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    buttonText: string;
    title: string;
    buttonUrl: LinkFieldValue;
    imageUrl: string;
    imageCaption?: string;
    openInNewTab?: boolean;
    extendedStyles?: InformationCardStyles;
    buttonAlignStyles: string;
}

type Props = OwnProps;

export interface InformationCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

export const informationCardStyles: InformationCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            min-height: 100%;
            width: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: 210px;
            margin-bottom: 20px;

            ${xScreen("lte", "md")} {
                height: 175px;
            }

            ${xScreen("lte", "sm")} {
                height: 160px;
            }
        `,
    },
    image: {
        css: css`
            width: 100%;
            max-height: 100%;
            border-radius: 40px 0 40px 0;
            object-fit: cover;
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "h3",
        css: css`
            margin-bottom: 20px;
        `,
    },
    link: {
        color: "text.link",
        hoverStyle: {
            color: getSystemColor("common.ui8"),
        },
        icon: {
            iconProps: {
                src: PaginationRight,
                size: 12,
                mergeCss: true,
                color: "tertiary",
                css: css`
                    svg {
                        width: 12px;
                        height: 12px;
                    }
                `,
            },
            position: "right",
        },
        typographyProps: {
            size: 16,
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        css: css`
            ${IconWrapper} {
                margin-left: 8px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const InfoCard: React.FC<Props> = ({
    buttonText,
    title,
    buttonUrl,
    imageUrl,
    imageCaption,
    openInNewTab = false,
    extendedStyles,
    buttonAlignStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(informationCardStyles, extendedStyles));

    const link = useGetLink(buttonUrl);

    const infoCardStyles = {
        css: css`
            ${buttonAlignStyles}
        `,
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={imageUrl} {...styles.image} altText={imageCaption || title} />
            </StyledWrapper>
            <Typography {...styles.title}>{title}</Typography>
            <StyledWrapper {...infoCardStyles}>
                <Link href={link.url} target={openInNewTab ? "_blank" : ""} {...styles.link} aria-label={`${buttonText} ${link.title}`}>
                    {buttonText}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default InfoCard;
